import React from 'react';
import { Warehouse } from 'lucide-react';
import { BlockShell, ci } from './_shared';
import { formatQtyDE } from 'core/utils/format';

// Verfügbarkeit: Bestand je Werk/Lagerort mit Status-Indikator (grün/gelb/rot).
// Zeilen werden nach stockType in getrennte Töpfe gruppiert.
const DOT = { green: 'bg-emerald-500', yellow: 'bg-amber-500', red: 'bg-red-500' };

// Anzeigereihenfolge der Töpfe
const STOCK_TYPE_ORDER = ['own', 'vendor_consignment', 'at_vendor'];

// i18n-Keys je Topf
const STOCK_TYPE_LABEL_KEY = {
  own: 'productDetail.stockOwn',
  vendor_consignment: 'productDetail.stockVendorConsignment',
  at_vendor: 'productDetail.stockAtVendor',
};

const BlockAvailability = ({ dto, t, isDarkMode }) => {
  const av = dto.availability || {};
  const c = ci(isDarkMode);
  const rows = Array.isArray(av.stockByLocation) ? av.stockByLocation : [];
  const unit = dto.identity && dto.identity.baseUnit;

  // Zeilen nach stockType gruppieren
  const groups = {};
  rows.forEach((s) => {
    const type = s.stockType || 'own';
    if (!groups[type]) groups[type] = [];
    groups[type].push(s);
  });

  // Reihenfolge: own → vendor_consignment → at_vendor → Rest
  const knownTypes = STOCK_TYPE_ORDER.filter((type) => groups[type]);
  const otherTypes = Object.keys(groups).filter((type) => !STOCK_TYPE_ORDER.includes(type));
  const orderedTypes = [...knownTypes, ...otherTypes];

  return (
    <BlockShell title={t('productDetail.availability')} icon={Warehouse} status={av.status} isDarkMode={isDarkMode} demoLabel={t('productDetail.demoFromSap')}>
      {rows.length > 0 ? (
        <div className="space-y-4">
          {orderedTypes.map((stockType) => {
            const typeRows = groups[stockType];
            if (!typeRows || typeRows.length === 0) return null;
            const labelKey = STOCK_TYPE_LABEL_KEY[stockType];
            return (
              <div key={stockType}>
                {/* Abschnittsüberschrift */}
                {labelKey && (
                  <p className={`text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
                    {t(labelKey)}
                  </p>
                )}
                <div className={`divide-y ${isDarkMode ? 'divide-white/10' : 'divide-slate-100'}`}>
                  {typeRows.map((s, i) => {
                    const isSonderbestand = s.vendor && s.vendor !== '';
                    return (
                      <div key={i} className="flex items-center justify-between py-2">
                        <span className="flex items-center gap-2">
                          <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${DOT[s.status] || 'bg-slate-400'}`} />
                          {isSonderbestand ? (
                            <span className={`text-sm ${c.value}`}>
                              {t('productDetail.stockVendor')} {s.vendor}
                            </span>
                          ) : (
                            <>
                              <span className={`text-sm ${c.value}`}>
                                {s.plantName || `${t('productDetail.plant')} ${s.plant}`}
                              </span>
                              {s.storageLocation && (
                                <span className={`text-xs ${c.label}`}>
                                  {s.locationName
                                    ? `${s.locationName} (${s.storageLocation})`
                                    : `${t('productDetail.storageLocation')} ${s.storageLocation}`}
                                </span>
                              )}
                            </>
                          )}
                        </span>
                        <span className={`text-sm ${c.value}`}>{formatQtyDE(s.qty)} {unit}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className={`text-sm ${c.sub}`}>{t('productDetail.availabilityEmpty')}</p>
      )}
    </BlockShell>
  );
};

export default BlockAvailability;
